import { financeInvoiceTotal } from '@veem/crm-schema'
import type { InvoiceStatus as SchemaInvoiceStatus } from '@veem/schema'
import { createSnapshotValue as createMutable } from '../../lib/snapshot-value.ts'
import { add, compare, isPositive, toMoney, vatIncluded } from '../lib/decimal.ts'
import { TODAY } from '../lib/format.ts'

export type InvoiceStatus = Exclude<SchemaInvoiceStatus, 'draft'>

/**
 * Spec §11.2: an invoice is its own record, linked to our legal entity, the client and (usually)
 * an order. One order can have several: a prepayment, then the balance. Payments are append-only;
 * a wrong payment is reversed by a compensating row with a reason, never edited away.
 */
export type InvoiceKind = 'full' | 'prepayment' | 'balance' | 'other'

export const INVOICE_KIND_LABELS: Record<InvoiceKind, string> = {
  full: 'На повну суму',
  prepayment: 'Передоплата',
  balance: 'Остаточний розрахунок',
  other: 'Без замовлення',
}

export type PaymentMethod = 'bank' | 'cash'

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  bank: 'Банк',
  cash: 'Готівка',
}

export const SENT_VIA = ['Email', 'Viber', 'Telegram', 'Особисто'] as const
export type SentVia = (typeof SENT_VIA)[number]

export interface InvoiceLine {
  description: string
  quantity: string
  unit: string
  /** Per unit, including VAT when the treatment has VAT. */
  unitPrice: string
}

export interface InvoicePayment {
  id: string
  /** Value date of the bank payment. */
  at: string
  /** Negative for a reversal. */
  amount: string
  method: PaymentMethod
  reference: string | null
  actorId: string
  recordedAt: string
  /** Set on the compensating row: which payment it reverses. */
  reversalOf: string | null
  reason: string | null
}

export interface InvoiceEvent {
  at: string
  actorId: string | null
  text: string
}

export interface ProtoInvoiceRecord {
  id: string
  version?: number
  total?: string
  paid?: string
  outstanding?: string
  vat?: string
  number: string
  kind: InvoiceKind
  sellerEntityId: string
  customerId: string
  dealId: string | null
  /** Snapshots taken from the deal when the invoice was created (spec §10). */
  currency: string
  vatName: string
  vatRate: string
  lines: InvoiceLine[]
  createdAt: string
  createdById: string
  issuedAt: string
  dueAt: string
  sentAt: string | null
  sentVia: SentVia | null
  file: { name: string; url?: string; source: 'upload' | 'generated' } | null
  payments: InvoicePayment[]
  events: InvoiceEvent[]
  comment: string | null
}

export const INVOICES: ProtoInvoiceRecord[] = createMutable([] as ProtoInvoiceRecord[])

export const invoiceTotal = (invoice: Pick<ProtoInvoiceRecord, 'lines' | 'total'>) =>
  invoice.total ?? financeInvoiceTotal(invoice.lines)

export const invoicePaid = (invoice: Pick<ProtoInvoiceRecord, 'payments' | 'paid'>) =>
  invoice.paid ?? toMoney(add('0', ...invoice.payments.map((payment) => payment.amount)))

/** VAT inside the total; display only, the real figure comes from the API (spec §10.1). */
export const invoiceVat = (
  invoice: Pick<ProtoInvoiceRecord, 'lines' | 'vatRate' | 'vat' | 'total'>,
) =>
  invoice.vat ??
  (isPositive(invoice.vatRate) ? vatIncluded(invoiceTotal(invoice), invoice.vatRate) : '0.00')

/** Status is derived from facts (issued, sent, paid, due date), so it can never drift. */
export const invoiceStatus = (invoice: ProtoInvoiceRecord): InvoiceStatus => {
  if (compare(invoicePaid(invoice), invoiceTotal(invoice)) >= 0) return 'paid'
  if (invoice.dueAt < TODAY) return 'overdue'
  return invoice.sentAt ? 'awaiting' : 'issued'
}
